import React, { useMemo, useState } from "react";
import { RefreshCw, Wallet, X } from "lucide-react";
import { Modal, MODAL_HEADER, MODAL_ACTIONS } from "../shared/Modal";
import { FormField } from "../shared/FormField";
import * as api from "../../api";

const MIN_WITHDRAWAL_BS = 500;
const COMMISSION_RATE = 0.035;

/* -----------------------------------------------------------------------------
 * WithdrawalRequestModal — reseller solicita retirar su "saldo a favor".
 * Módulo independiente: no toca pedidos ni pagos, solo llama a
 * POST /api/withdrawals. El backend bloquea el monto de inmediato.
 * --------------------------------------------------------------------------- */
export function WithdrawalRequestModal({ open, onClose, availableBs, onSuccess, showToast }) {
  const [amount, setAmount] = useState("");
  const [cedula, setCedula] = useState("");
  const [telefono, setTelefono] = useState("");
  const [banco, setBanco] = useState("");
  const [busy, setBusy] = useState(false);

  const amountNum = parseFloat(amount) || 0;
  const commissionBs = useMemo(() => Math.round(amountNum * COMMISSION_RATE * 100) / 100, [amountNum]);
  const netBs = useMemo(() => Math.round((amountNum - commissionBs) * 100) / 100, [amountNum, commissionBs]);

  const belowMin = amountNum > 0 && amountNum < MIN_WITHDRAWAL_BS;
  const overBalance = amountNum > Number(availableBs || 0);
  const canSubmit = amountNum >= MIN_WITHDRAWAL_BS && !overBalance && cedula.trim() && telefono.trim() && banco.trim();

  if (!open) return null;

  const reset = () => {
    setAmount(""); setCedula(""); setTelefono(""); setBanco("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || busy) return;
    setBusy(true);
    try {
      await api.requestWithdrawal({
        amountBs: amountNum, cedula: cedula.trim(), telefono: telefono.trim(), banco: banco.trim(),
      });
      showToast?.("Solicitud enviada", "Tu retiro quedó pendiente de revisión.", "success");
      reset();
      onClose();
      await onSuccess?.();
    } catch (err) {
      showToast?.("No se pudo solicitar", err.message || "Intenta de nuevo.", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={() => !busy && onClose()} size="md" testId="withdrawal-request-modal">
      <form className="space-y-4" onSubmit={submit}>
        <div className={MODAL_HEADER}>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-emerald-950/60 border border-emerald-500 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">Retirar Saldo</h3>
              <p className="text-[10px] font-mono text-zinc-500">Disponible: Bs {Number(availableBs || 0).toFixed(2)}</p>
            </div>
          </div>
          <button type="button" onClick={() => !busy && onClose()} data-testid="withdrawal-request-close-btn" className="text-zinc-400 hover:text-white bg-zinc-800 p-2 rounded-full">
            <X className="w-4 h-4" />
          </button>
        </div>

        <FormField label="Monto a retirar (Bs)">
          <input
            type="number" step="0.01" min="0"
            data-testid="withdrawal-amount-input"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={`Mínimo Bs ${MIN_WITHDRAWAL_BS}`}
            className="w-full bg-zinc-950 border border-zinc-700 rounded-xl p-2.5 text-sm text-white focus:outline-none focus:border-emerald-500"
          />
        </FormField>
        {belowMin && (
          <p data-testid="withdrawal-min-amount-error" className="text-xs text-red-400">El retiro mínimo es Bs {MIN_WITHDRAWAL_BS}.</p>
        )}
        {overBalance && (
          <p data-testid="withdrawal-over-balance-error" className="text-xs text-red-400">El monto supera tu saldo disponible.</p>
        )}

        {amountNum > 0 && (
          <div data-testid="withdrawal-commission-summary" className="p-3 rounded-xl bg-zinc-900 border border-zinc-700 space-y-1 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Comisión (3.5%)</span>
              <strong className="text-amber-300" data-testid="withdrawal-commission-value">Bs {commissionBs.toFixed(2)}</strong>
            </div>
            <div className="flex items-center justify-between pt-1 border-t border-zinc-800">
              <span className="text-zinc-300">Recibirás</span>
              <strong className="text-emerald-300 text-base" data-testid="withdrawal-net-value">Bs {netBs.toFixed(2)}</strong>
            </div>
          </div>
        )}

        <FormField label="Cédula">
          <input
            type="text" data-testid="withdrawal-cedula-input"
            value={cedula} onChange={(e) => setCedula(e.target.value)}
            placeholder="V-12345678"
            className="w-full bg-zinc-950 border border-zinc-700 rounded-xl p-2.5 text-sm text-white focus:outline-none focus:border-emerald-500"
          />
        </FormField>
        <FormField label="Teléfono">
          <input
            type="tel" data-testid="withdrawal-telefono-input"
            value={telefono} onChange={(e) => setTelefono(e.target.value)}
            placeholder="+58 412 1234567"
            className="w-full bg-zinc-950 border border-zinc-700 rounded-xl p-2.5 text-sm text-white focus:outline-none focus:border-emerald-500"
          />
        </FormField>
        <FormField label="Banco">
          <input
            type="text" data-testid="withdrawal-banco-input"
            value={banco} onChange={(e) => setBanco(e.target.value)}
            placeholder="Ej. Banesco"
            className="w-full bg-zinc-950 border border-zinc-700 rounded-xl p-2.5 text-sm text-white focus:outline-none focus:border-emerald-500"
          />
        </FormField>

        <div className={MODAL_ACTIONS}>
          <button
            type="button" onClick={() => !busy && onClose()} disabled={busy}
            data-testid="withdrawal-request-cancel-btn"
            className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white font-bold py-3 rounded-xl text-xs disabled:opacity-40">
            Cancelar
          </button>
          <button
            type="submit" disabled={!canSubmit || busy}
            data-testid="withdrawal-request-submit-btn"
            className="flex-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white font-bold py-3 rounded-xl text-xs shadow-lg flex items-center justify-center gap-2">
            {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />}
            <span>{busy ? "Enviando..." : "Solicitar Retiro"}</span>
          </button>
        </div>
      </form>
    </Modal>
  );
}
